import React, { useMemo, useState } from "react";

const WeatherForecast = ({ bothy, weatherState }) => {
  const { data, snow, loading, error, meta } = weatherState;
  const dailyUnits = {
    ...(data?.daily_units ?? {}),
    ...(snow?.daily_units ?? {})
  };
  const hourlyUnits = {
    ...(data?.hourly_units ?? {}),
    ...(snow?.hourly_units ?? {})
  };

  const forecastDays = useMemo(
    () => buildDailyForecastEntries(data, snow, dailyUnits),
    [data, snow, dailyUnits]
  );
  const [selectedDayIndex, setSelectedDayIndex] = useState(null);

  const hourlyEntries = useMemo(
    () => buildHourlyForecastEntries(data, snow, selectedDayIndex, hourlyUnits),
    [data, snow, selectedDayIndex, hourlyUnits]
  );

  const handleDayClick = index => {
    setSelectedDayIndex(prev => (prev === index ? null : index));
  };

  return (
    <div className="weather-section">
      {meta?.fetchedAt && (
        <div className="helper-text">
          Weather cached at {new Date(meta.fetchedAt).toLocaleString()}
        </div>
      )}

      {loading && (
        <div className="loading-spinner">Loading weather data...</div>
      )}
      {error && <div className="error-message">{error}</div>}

      {!loading &&
        !error &&
        forecastDays.length === 0 && (
          <div className="helper-text">
            No cached forecast available for this bothy.
          </div>
        )}

      {forecastDays.length > 0 && (
        <div className="weather-forecast">
          {forecastDays.map(day => {
            const isExpanded = day.index === selectedDayIndex;
            return (
              <div
                key={`${day.date}-${day.index}`}
                className={`weather-card${
                  isExpanded ? " is-selected" : ""
                } is-clickable`}
                role="button"
                tabIndex={0}
                onClick={() => handleDayClick(day.index)}
                onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleDayClick(day.index);
                  }
                }}
              >
                <div className="weather-card-header">
                  <div className="day-name eyebrow">{day.day}</div>
                  <div className="day-date eyebrow">{day.date}</div>
                </div>

                <div className="weather-card-content">
                  <div className="weather-brief">
                    <div className="weather-icon">
                      <i
                        className={`fas ${getWeatherIconClass(
                          day.weatherCode
                        )}`}
                      />
                    </div>
                    <div className="condition">{day.condition}</div>
                    <div className="temperature-range">
                      {day.temperatureRange}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="weather-card-details">
                      <div className="weather-details-grid">
                        <div>
                          <span className="weather-label">Precipitation:</span>
                          <span className="weather-value">
                            {day.precipitation}
                          </span>
                        </div>
                        <div>
                          <span className="weather-label">Rain:</span>
                          <span className="weather-value">{day.rainfall}</span>
                        </div>
                        <div>
                          <span className="weather-label">Snowfall:</span>
                          <span className="weather-value">{day.snowfall}</span>
                        </div>
                        <div>
                          <span className="weather-label">Snow depth:</span>
                          <span className="weather-value">{day.snowDepth}</span>
                        </div>
                        <div>
                          <span className="weather-label">Wind:</span>
                          <span className="weather-value">{day.wind}</span>
                        </div>
                      </div>
                      <div className="sun-times">
                        <div>
                          <span className="sun-label">
                            <i className="fas fa-sun" /> Rise:
                          </span>
                          <span>{day.sunrise}</span>
                        </div>
                        <div>
                          <span className="sun-label">
                            <i className="fas fa-moon" /> Set:
                          </span>
                          <span>{day.sunset}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {isExpanded && (
                  <div className="weather-card-hourly">
                    {hourlyEntries.length > 0 ? (
                      <div className="hourly-grid">
                        {hourlyEntries.map(entry => (
                          <div key={entry.time} className="hourly-row">
                            <div className="hourly-time">{entry.time}</div>
                            <div className="hourly-value">
                              Temp: {entry.temperature}
                            </div>
                            <div className="hourly-value">
                              Precip: {entry.precipitation}
                            </div>
                            <div className="hourly-value">
                              Snow: {entry.snowfall}
                            </div>
                            <div className="hourly-value">
                              Depth: {entry.snowDepth}
                            </div>
                            <div className="hourly-value">
                              Wind: {entry.wind}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="helper-text">
                        No hourly data available for this day.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const buildDailyForecastEntries = (data, snow, dailyUnits = {}) => {
  const times = data?.daily?.time ?? snow?.daily?.time;
  if (!Array.isArray(times)) {
    return [];
  }

  const entries = times.map((isoTime, index) => {
    const date = new Date(isoTime);
    const weatherCode = data?.daily?.weathercode?.[index];
    const minTemp = data?.daily?.temperature_2m_min?.[index];
    const maxTemp = data?.daily?.temperature_2m_max?.[index];
    const precipitation = data?.daily?.precipitation_sum?.[index];
    const rainfall = data?.daily?.rain_sum?.[index];
    const snowfall =
      snow?.daily?.snowfall_sum ?? data?.daily?.snowfall_sum?.[index];
    const snowDepth =
      snow?.daily?.snow_depth_max ?? data?.daily?.snow_depth_max?.[index];
    const windSpeed = data?.daily?.wind_speed_10m_max?.[index];
    const sunrise = data?.daily?.sunrise?.[index];
    const sunset = data?.daily?.sunset?.[index];

    return {
      index,
      day: date.toLocaleDateString("en-US", { weekday: "long" }),
      date: date.toLocaleDateString("en-GB"),
      weatherCode,
      condition: getWeatherCondition(weatherCode),
      temperatureRange: formatTemperatureRange(minTemp, maxTemp),
      precipitation: formatMeasurement(
        precipitation,
        dailyUnits.precipitation_sum
      ),
      rainfall: formatMeasurement(rainfall, dailyUnits.rain_sum),
      snowfall: formatMeasurement(snowfall, dailyUnits.snowfall_sum),
      snowDepth: formatMeasurement(
        convertToMmIfMeters(snowDepth, dailyUnits.snow_depth_max),
        resolveDepthUnit(dailyUnits.snow_depth_max)
      ),
      wind: formatMeasurement(windSpeed, dailyUnits.wind_speed_10m_max),
      sunrise: sunrise ? formatTime(sunrise) : "N/A",
      sunset: sunset ? formatTime(sunset) : "N/A",
      hasHourly: Array.isArray(data?.hourly?.time ?? snow?.hourly?.time)
    };
  });

  return entries;
};

const buildHourlyForecastEntries = (data, snow, dayIndex, hourlyUnits = {}) => {
  if (dayIndex === null || dayIndex === undefined) {
    return [];
  }

  const dailyTimes = data?.daily?.time ?? snow?.daily?.time;
  const hourlyTimes = data?.hourly?.time ?? snow?.hourly?.time;
  if (!Array.isArray(dailyTimes) || !Array.isArray(hourlyTimes)) {
    return [];
  }

  const targetDate = dailyTimes[dayIndex];
  if (!targetDate) {
    return [];
  }

  const targetPrefix = targetDate.slice(0, 10);
  const indices = hourlyTimes
    .map((time, index) => ({ time, index }))
    .filter(item => item.time.startsWith(targetPrefix));

  const {
    temperature_2m = [],
    precipitation = [],
    snowfall = [],
    snow_depth = [],
    wind_speed_10m = []
  } =
    data?.hourly ?? {};

  const snowHourly = snow?.hourly ?? {};

  return indices.map(({ time, index }) => ({
    time: formatTime(time),
    temperature: formatMeasurement(
      temperature_2m[index],
      hourlyUnits.temperature_2m
    ),
    precipitation: formatMeasurement(
      precipitation[index],
      hourlyUnits.precipitation
    ),
    snowfall: formatMeasurement(
      (snowHourly.snowfall ?? snowfall)[index],
      hourlyUnits.snowfall
    ),
    snowDepth: formatMeasurement(
      convertToMmIfMeters(
        (snowHourly.snow_depth ?? snow_depth)[index],
        hourlyUnits.snow_depth
      ),
      resolveDepthUnit(hourlyUnits.snow_depth)
    ),
    wind: formatMeasurement(wind_speed_10m[index], hourlyUnits.wind_speed_10m)
  }));
};

const getWeatherIconClass = weatherCode => {
  if (weatherCode === 0 || weatherCode === 1) return "fa-sun";
  if (weatherCode === 2) return "fa-cloud-sun";
  if (weatherCode === 3) return "fa-cloud";
  if ([45, 48].includes(weatherCode)) return "fa-smog";
  if ([51, 53, 55, 56, 57].includes(weatherCode)) return "fa-cloud-rain";
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(weatherCode))
    return "fa-umbrella";
  if ([71, 73, 75, 77, 85, 86].includes(weatherCode)) return "fa-snowflake";
  if ([95, 96, 99].includes(weatherCode)) return "fa-bolt";
  return "fa-cloud";
};

const getWeatherCondition = code => {
  const weatherCodes = {
    0: "Clear sky",
    1: "Mainly clear",
    2: "Partly cloudy",
    3: "Overcast",
    45: "Fog",
    48: "Depositing rime fog",
    51: "Light drizzle",
    53: "Moderate drizzle",
    55: "Dense drizzle",
    56: "Light freezing drizzle",
    57: "Dense freezing drizzle",
    61: "Slight rain",
    63: "Moderate rain",
    65: "Heavy rain",
    66: "Light freezing rain",
    67: "Heavy freezing rain",
    71: "Slight snow fall",
    73: "Moderate snow fall",
    75: "Heavy snow fall",
    77: "Snow grains",
    80: "Slight rain showers",
    81: "Moderate rain showers",
    82: "Violent rain showers",
    85: "Slight snow showers",
    86: "Heavy snow showers",
    95: "Thunderstorm",
    96: "Thunderstorm with slight hail",
    99: "Thunderstorm with heavy hail"
  };

  return weatherCodes[code] || "Unknown";
};

const formatTemperatureRange = (min, max) => {
  if (Number.isFinite(min) && Number.isFinite(max)) {
    return `${Math.round(min)}°C - ${Math.round(max)}°C`;
  }
  return "N/A";
};

const formatMeasurement = (value, unit = "") => {
  if (Number.isFinite(value)) {
    const suffix = unit ? ` ${unit}` : "";
    return `${value.toFixed(1)}${suffix}`;
  }
  return "N/A";
};

const convertToMmIfMeters = (value, unit) => {
  if (!Number.isFinite(value)) return value;
  if (!unit) return value;
  if (unit.toLowerCase() === "m") {
    return value * 1000;
  }
  if (unit.toLowerCase() === "cm") {
    return value * 10;
  }
  return value;
};

const resolveDepthUnit = unit => {
  if (!unit) return "";
  const lower = unit.toLowerCase();
  if (lower === "m" || lower === "cm") {
    return "mm";
  }
  return unit;
};

const formatTime = isoString => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }
  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
};

const formatCoordinate = value => {
  if (!Number.isFinite(value)) {
    return "N/A";
  }
  return Number(value).toFixed(4);
};

export default WeatherForecast;
